import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import WaveSurfer from "wavesurfer.js";

/* Hooks */
import { useThunk } from "../../hooks/useThunk";
import { getSignedUrl } from "../../store";
import { getSpecificUserPaymentHistory, checkEligibility } from "../../services/subscriptionThunks";
import showToast from "../../utils/toastNotifications";

/* Supreme edits context */
import { useSupremeEditsPlayer } from "../../contexts/SupremeEditsContext";

/* Assets */
import LogoImage from "../../assets/icons/Logo 2.webp";
import BackwardIcon from "../../assets/icons/player/backward.svg";
import PlayIcon from "../../assets/icons/player/play.svg";
import PauseIcon from "../../assets/icons/player/pause.svg";
import ForwardIcon from "../../assets/icons/player/forward.svg";
import LoadingIcon from "../../assets/icons/player/loading.svg";

/* Components */
import PlayerQueue from "../Popups/PlayerQueue";
import PlayerVersions from "../Popups/PlayerVersions";
import SubscriptionModal from "../Popups/SubscriptionModal";

// Helper function to format artist strings
const renderArtistsString = (
  mainArtists = [],
  featuringArtists = [],
  additionalMainArtists = []
) => {
  const formatList = (list) => {
    if (list?.length === 0) return "";
    if (list?.length === 1) return list[0];
    if (list?.length === 2) return `${list[0]} & ${list[1]}`;
    return `${list.slice(0, -1).join(", ")} & ${list[list?.length - 1]}`;
  };

  // Combine main artists with additional main artists (same as in getUserFavorites and latestTwelveTracks)
  const originalMainArtists = Array.isArray(mainArtists)
    ? mainArtists
    : [mainArtists];
  const additionalArtists =
    additionalMainArtists && Array.isArray(additionalMainArtists)
      ? additionalMainArtists
      : [];

  const combinedMainArtists = [...originalMainArtists, ...additionalArtists];

  const main = formatList(combinedMainArtists);
  const feat = formatList(
    Array.isArray(featuringArtists) ? featuringArtists : [featuringArtists]
  );

  return feat ? `${main} ft. ${feat}` : main;
};

const SupremeEditsPlayer = () => {
  // Refs for waveform container and WaveSurfer instance
  const waveformLgRef = useRef(null);
  const wavesurfer = useRef(null);
  const navigate = useNavigate();
  const isSeekingProgrammatically = useRef(false);

  const [doGetSignedUrl, isGettingSignedUrl, errorGettingSignedUrl] =
    useThunk(getSignedUrl);
  const [doGetSpecificUserPaymentHistory] = useThunk(
    getSpecificUserPaymentHistory
  );
  const [doCheckEligibility] = useThunk(checkEligibility);

  // Context values from SupremeEditsPlayer context
  const {
    currentTrack,
    handleNext,
    handlePrev,
    isPlaying,
    setIsPlaying,
    supremeCycle,
    editIndex,
    versionIndex,
    setVersionIndex,
    setTrackLoading,
    playerQueue,
    isManualPlay,
    setIsManualPlay,
    playingRowType,
  } = useSupremeEditsPlayer();

  // State variables for player functionality
  const [playingTrack, setPlayingTrack] = useState(null);
  const [peaks, setPeaks] = useState(null);
  const [trackVersion, setTrackVersion] = useState(null);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [isQueuePopupOpen, setIsQueuePopupOpen] = useState(false);
  const [isVersionsPopupOpen, setIsVersionsPopupOpen] = useState(false);
  const [isFullHeight, setIsFullHeight] = useState(false);
  const [isWaitingForConnection, setIsWaitingForConnection] = useState(false);
  const [isInitialPlay, setIsInitialPlay] = useState(true);
  const [coverKey, setCoverKey] = useState("");
  const [hasActiveSubscription, setHasActiveSubscription] = useState(true); // Default to true to avoid blocking before check
  const [previewCount, setPreviewCount] = useState(0);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showLockedTooltip, setShowLockedTooltip] = useState(false);
  const [modalSeenInSession, setModalSeenInSession] = useState(
    () => sessionStorage.getItem("subscriptionModalShown") === "true"
  );

  // Refs for maintaining state across renders
  const playerQueueRef = useRef(supremeCycle);
  const autoPlayRef = useRef(autoPlay);
  const lastLoadedUrl = useRef(null);
  const lastClickRef = useRef(Date.now());
  const previewLimitWarningShown = useRef(false);
  const lastCoverUrl = useRef(null);
  const shouldAutoplayRef = useRef(false);
  // Ref for the wall-clock setTimeout used to enforce the 45-second preview limit.
  // Using setTimeout (not audioprocess) so the cutoff fires even when the tab is
  // in the background (Chrome throttles timeupdate/audioprocess for inactive tabs).
  const previewTimerRef = useRef(null);

  // State variables for UI elements
  const [coverImage, setCoverImage] = useState({ error: true, url: LogoImage });
  const [coverImageReady, setCoverImageReady] = useState(false);
  const [trackTitle, setTrackTitle] = useState(null);
  const [artists, setArtists] = useState(null);
  const [editTitle, setEditTitle] = useState(null);

  useEffect(() => {
    // Load autoPlay setting from localStorage
    const storedAutoPlay = localStorage.getItem("autoPlay");
    if (storedAutoPlay) {
      setAutoPlay(JSON.parse(storedAutoPlay));
    }

    // Network connection monitoring
    const handleOnline = () => {
      if (isWaitingForConnection && autoPlayRef.current) {
        setIsWaitingForConnection(false);
        handleNext();
      }
    };

    const handleOffline = () => {
      // Connection lost - we'll wait for it to come back
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [isWaitingForConnection]);

  // Check eligibility using checkEligibility when a track/version is available
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      if (!currentTrack) return; // wait until we have a track to check

      try {
        const edit_info = currentTrack.editInfos?.[editIndex] || {};
        const payload = {
          trackId: currentTrack.trackId || currentTrack._id,
          editId: edit_info?._id || editIndex,
          remixerId:
            currentTrack.remixerId || currentTrack.remixer?._id || null,
          versionId: versionIndex !== undefined ? versionIndex : 0,
        };

        const result = await doCheckEligibility(payload);
        console.log("result", result)
        const eligible = result?.response?.eligibility;
        console.log("Eligibility check result:", { payload, result, eligible });
        setHasActiveSubscription(eligible);

        if (!eligible) {
          const storedCount = sessionStorage.getItem("previewTrackCount");
          setPreviewCount(storedCount ? parseInt(storedCount, 10) : 0);
        }
      } catch (error) {
        console.error("Failed to check eligibility:", error);
        setHasActiveSubscription(false);
      }
    };

    checkSubscriptionStatus();
  }, [currentTrack, editIndex, versionIndex]);

  useEffect(() => {
    if (!playingTrack) return;

    // Only create instance if it doesn't exist
    if (wavesurfer.current && !wavesurfer.current.isDestroyed) {
      return;
    }

    setAudioLoaded(false);
    setCurrentTime(0);
    setDuration(0);
    setTrackLoading(true);

    // Destroy existing instance if it exists
    if (wavesurfer.current) {
      wavesurfer.current.destroy();
      wavesurfer.current = null;
    }

    // Use a single container that will be styled responsively
    const container = waveformLgRef.current;
    if (!container) return;

    const commonOptions = {
      waveColor: "#8D8D8D",
      progressColor: [
        "#52FAF0",
        "#197AD0",
        "#197AD0",
        "#197AD0",
        "#197AD0",
        "#197AD0",
        "#197AD0",
      ],
      barWidth: 2,
      barGap: 3,
      barRadius: 15,
      height: 30,
      cursorWidth: 0,
      normalize: true,
      responsive: true,
      sampleRate: 3000,
      hideScrollbar: true,
    };

    // Create single WaveSurfer instance
    wavesurfer.current = WaveSurfer.create({
      ...commonOptions,
      container: container,
    });

    const loadTrack = async () => {
      try {
        const result = await doGetSignedUrl({ path: playingTrack });
        const url = result?.response?.data?.url;
        if (!result.success || !url)
          throw new Error("Failed to fetch audio file");

        let cleanPath = url;
        const parsedUrl = new URL(url);
        if (parsedUrl.origin === window.location.origin) {
          const signature = parsedUrl.searchParams.get("sig");
          const expires = parsedUrl.searchParams.get("expires");
          document.cookie = `audio_sig=${signature}; path=/; Secure; SameSite=None`;
          document.cookie = `audio_exp=${expires}; path=/; Secure; SameSite=None`;
          cleanPath = parsedUrl.origin + parsedUrl.pathname;
        }

        const safePeaks =
          peaks && Array.isArray(peaks)
            ? JSON.parse(JSON.stringify(peaks))
            : null;

        // Load the track
        await wavesurfer.current.load(cleanPath, safePeaks);
      } catch (error) {
        console.error("Failed to load audio with auth header:", error);
        setTrackLoading(false);
        setIsPlaying(false);
        setAudioLoaded(false);

        // Check if it's a network error and handle accordingly
        if (autoPlayRef.current) {
          if (!navigator.onLine) {
            // No connection - wait for it to be restored
            setIsWaitingForConnection(true);
          } else {
            // Connected but other error - skip to next
            handleNext();
          }
        }
      }
    };

    loadTrack();

    wavesurfer.current.on("ready", () => {
      if (wavesurfer.current.isDestroyed) return;
      setAudioLoaded(true);
      setTrackLoading(false);
      setDuration(wavesurfer.current.getDuration());

      // Play if we previously intended to keep playing, or autoplay is on,
      // or a manual play action triggered the change.
      if (shouldAutoplayRef.current) {
        wavesurfer.current.play().catch(() => {});
        setIsPlaying(true);
        shouldAutoplayRef.current = false; // reset
        if (isManualPlay) setIsManualPlay(false);
      }
    });

    // ── Preview-limit helpers ────────────────────────────────────────────────
    // We deliberately use wall-clock setTimeout instead of the audioprocess
    // event to stop playback.  Chrome throttles timeupdate (and therefore
    // audioprocess) for background tabs, which caused the 45-second cut-off
    // to be delayed until the user switched back to the tab.
    // A setTimeout scheduled in an active tab continues to fire on time even
    // when the user moves to another tab, so the limit is enforced precisely
    // and consistently across Chrome and Firefox.

    const clearPreviewTimer = () => {
      if (previewTimerRef.current !== null) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
    };

    const stopPlaybackAtLimit = () => {
      if (wavesurfer.current && !wavesurfer.current.isDestroyed) {
        wavesurfer.current.pause();
        setIsPlaying(false);
      }
      // Force the display to show exactly 00:45, since the last audioprocess
      // update is typically a fraction of a second behind and Math.floor would
      // otherwise render it as 00:44.
      setCurrentTime(45);
      if (!previewLimitWarningShown.current) {
        showToast("info", "Preview limit reached. Subscribe to hear full tracks!");
        previewLimitWarningShown.current = true;
      }
    };

    const schedulePreviewStop = () => {
      clearPreviewTimer();
      if (!wavesurfer.current || wavesurfer.current.isDestroyed) return;
      const played = wavesurfer.current.getCurrentTime();
      // Math.ceil ensures we never round the delay down by a sub-millisecond
      // fraction, which would cause the timer to fire just before the 45-second
      // mark and the audio to stop at a displayed time of 44 seconds.
      const remainingMs = Math.ceil((45 - played) * 1000);
      if (remainingMs <= 0) {
        stopPlaybackAtLimit();
        return;
      }
      previewTimerRef.current = setTimeout(stopPlaybackAtLimit, remainingMs);
    };
    // ── End preview-limit helpers ────────────────────────────────────────────

    wavesurfer.current.on("play", () => {
      setIsPlaying(true);
      if (!hasActiveSubscription) {
        schedulePreviewStop();
      }
    });
    wavesurfer.current.on("pause", () => {
      setIsPlaying(false);
      clearPreviewTimer();
    });
    wavesurfer.current.on("audioprocess", () => {
      if (wavesurfer.current.isPlaying()) {
        // Update the visual progress; the 45-second stop is handled by
        // schedulePreviewStop() via setTimeout — no polling needed here.
        setCurrentTime(wavesurfer.current.getCurrentTime());
      }
    });

    wavesurfer.current.on("finish", () => {
      if (autoPlayRef.current) {
        handleSafeMove("next");
      } else {
        setIsPlaying(false);
      }
    });

    // Handle errors during playback
    wavesurfer.current.on("error", (error) => {
      console.error("WaveSurfer error:", error);
      setTrackLoading(false);
      setIsPlaying(false);
      setAudioLoaded(false);

      // Check if it's a network error and handle accordingly
      if (autoPlayRef.current) {
        if (!navigator.onLine) {
          // No connection - wait for it to be restored
          setIsWaitingForConnection(true);
        } else {
          // Connected but other error - skip to next
          handleSafeMove("next");
        }
      }
    });

    // Add seeking events for drag-to-seek functionality
    const handleSeek = (targetTime) => {
      // Ignore this event if we're programmatically seeking to prevent loops
      if (isSeekingProgrammatically.current) {
        return;
      }
      
      const duration = wavesurfer.current.getDuration();
      
      // Preview limitation for non-subscribers - restrict seeking beyond 45 seconds
      // Only apply if we have a valid duration and user doesn't have subscription
      if (!hasActiveSubscription && duration > 0 && targetTime > 45) {
        // Set flag to prevent recursive seeking events
        isSeekingProgrammatically.current = true;
        
        // Show warning toast
        showToast(
          "warning",
          "Preview limited to 45 seconds. Subscribe to access full tracks!"
        );
        
        // Snap back to 45 seconds
        wavesurfer.current.seekTo(45 / duration);
        setCurrentTime(45);
        
        // Reset flag after a short delay to allow programmatic seek to complete
        setTimeout(() => {
          isSeekingProgrammatically.current = false;
        }, 50);
        return;
      }
      
      // For valid seeks within the preview range, update the time and
      // reschedule the wall-clock timer so it fires at exactly 45 s from now.
      setCurrentTime(targetTime);
      if (!hasActiveSubscription && wavesurfer.current.isPlaying()) {
        schedulePreviewStop();
      }
    };

    wavesurfer.current.on("seeking", handleSeek);

    return () => {
      clearPreviewTimer();
      wavesurfer.current?.unAll();
      wavesurfer.current?.destroy();
      wavesurfer.current = null;
    };
  }, [playingTrack, hasActiveSubscription]);

  // Handle autoplay when new track is loaded via handleNext/handlePrev
  useEffect(() => {
    // Only auto-play if:
    // 1. The track is loaded
    // 2. Auto-play is enabled AND it's an automatic transition (not manual selection)
    // 3. The track was loaded via handleNext/handlePrev (tracked by isPlaying being true before load)
    if (audioLoaded && autoPlayRef.current && isPlaying && !isManualPlay) {
      if (wavesurfer.current && !wavesurfer.current.isPlaying()) {
        wavesurfer.current.play().catch(() => {});
        setIsPlaying(true);
      }
    }
  }, [audioLoaded, playingTrack, isPlaying, isManualPlay]);

  // Handle play/pause state changes when isPlaying changes from context
  useEffect(() => {
    if (!audioLoaded || !wavesurfer.current) return;

    if (isPlaying && !wavesurfer.current.isPlaying()) {
      wavesurfer.current.play().catch(() => {});
    } else if (!isPlaying && wavesurfer.current.isPlaying()) {
      wavesurfer.current.pause();
    }
  }, [isPlaying, audioLoaded]);

  useEffect(() => {
    if (!currentTrack) return;

    const wasPlaying = wavesurfer.current?.isPlaying?.() || isPlaying;
    shouldAutoplayRef.current =
      wasPlaying || autoPlayRef.current || isManualPlay;

    if (wavesurfer.current) wavesurfer.current?.stop();

    const edit_info = currentTrack.editInfos?.[editIndex] || {};

    // Use the versionIndex from props/context directly without overriding it
    let manualVersionIndex = versionIndex !== undefined ? versionIndex : 0;

    // Validate that the versionIndex is within bounds
    const maxVersionIndex = (edit_info?.versions?.length || 1) - 1;
    if (manualVersionIndex > maxVersionIndex) {
      manualVersionIndex = 0;
    }

    console.log("Player useEffect:", {
      trackId: currentTrack.trackId || currentTrack._id,
      editIndex,
      versionIndex,
      manualVersionIndex,
      maxVersionIndex,
      versionsCount: edit_info?.versions?.length,
      versions: edit_info?.versions?.map((v) => v.versionType),
      playingRowType,
    });

   // // Fallback: try to find by versionType if it exists on currentTrack
    // if (currentTrack.versionType) {
    //   const foundIndex = (edit_info?.versions || []).findIndex(
    //     (item) => item.versionType === currentTrack.versionType
    //   );
    //   if (foundIndex !== -1) {
    //     manualVersionIndex = foundIndex;
    //   }
    // }

    const trackVersion =
      edit_info?.versions?.[manualVersionIndex]?.versionType || "";
    const audioFile =
      edit_info?.versions?.[manualVersionIndex]?.audioFile || "";

    const peaks = edit_info?.versions?.[manualVersionIndex]?.peaks || "";

    if (lastLoadedUrl.current === audioFile || !audioFile) return;

    // Reset preview warning flag when loading a new track
    previewLimitWarningShown.current = false;
    
    // Track preview count for non-subscribers and show modal after 3-5 tracks
    if (!hasActiveSubscription) {
      const newCount = previewCount + 1;
      setPreviewCount(newCount);
      sessionStorage.setItem('previewTrackCount', newCount.toString());
      
      // Show subscription modal after 4th or 5th track (randomly choose between 4-5)
      const modalShown = sessionStorage.getItem('subscriptionModalShown');
      if (!modalShown && (newCount === 4 || newCount === 5)) {
        setTimeout(() => {
          setShowSubscriptionModal(true);
          sessionStorage.setItem('subscriptionModalShown', 'true');
          setModalSeenInSession(true);
        }, 2000); // Show after 2 seconds of playback
      }
    }

    const trackTitle = currentTrack.trackTitle || "Unknown Track";
    const coverArt = currentTrack.coverArt.url || currentTrack.coverArt;

    const mainArtists = currentTrack.artists || "";
    const additionalMainArtists = edit_info.additionalMainArtists || [];
    const featuredArtists = edit_info.featuredArtists || "";
    const editTitle = edit_info.editTitle || "";

    const artists = renderArtistsString(
      mainArtists,
      featuredArtists,
      additionalMainArtists
    );

    lastLoadedUrl.current = audioFile;

    setCoverImageReady(false);
    setArtists(artists);
    setTrackTitle(trackTitle);
    setEditTitle(editTitle);
    setTrackVersion(trackVersion);
  // REMOVE THIS LINE: Don't set versionIndex here as it conflicts with the selection
  // setVersionIndex(manualVersionIndex);
    setCoverKey(
      `${currentTrack._id || "trk"}-${editIndex}-${manualVersionIndex}`
    );

    if (coverArt) {
      setCoverImage({ error: false, url: coverArt });
      if (lastCoverUrl.current === coverArt) {
        setCoverImageReady(true);
      } else {
        setCoverImageReady(false);
        lastCoverUrl.current = coverArt;
      }
    } else {
      setCoverImage({ error: true, url: LogoImage });
      setCoverImageReady(false);
      lastCoverUrl.current = null;
    }

    setPeaks(peaks);
    setPlayingTrack(audioFile);
  }, [currentTrack, editIndex, versionIndex]); // Remove setVersionIndex from dependencies

  useEffect(() => {
    playerQueueRef.current = supremeCycle;
  }, [supremeCycle]);

  useEffect(() => {
    autoPlayRef.current = autoPlay;
    localStorage.setItem("autoPlay", JSON.stringify(autoPlay));
  }, [autoPlay]);

  useEffect(() => {
    if (isQueuePopupOpen || isVersionsPopupOpen) {
      // If either popup is open, set full height immediately
      setIsFullHeight(true); // immediately go full height when opening
    } else {
      // delay height change until after fade-out animation
      const timeout = setTimeout(() => setIsFullHeight(false), 500); // match backdrop fade duration
      return () => clearTimeout(timeout);
    }
  }, [isQueuePopupOpen, isVersionsPopupOpen]);

  // Function to toggle play/pause
  const togglePlay = () => {
    const isPlayingNow = wavesurfer.current?.isPlaying();

    if (isPlayingNow) {
      wavesurfer.current?.pause();
      setIsPlaying(false);
    } else {
      // Check if non-subscriber is trying to play beyond 45-second preview limit
      if (!hasActiveSubscription && currentTime >= 45) {
        showToast(
          "info",
          "Preview limit reached. Subscribe to hear full tracks!"
        );
        return; // Prevent playback
      }
      
      setIsManualPlay(true); // Signal this is a manual play action
      wavesurfer.current?.play().catch(() => {});
      setIsPlaying(true);
    }
  };

  // Helper function to format time in mm:ss
  const formatTime = (time) => {
    const min = Math.floor(time / 60);
    const sec = Math.floor(time % 60);
    return `${min.toString().padStart(2, "0")}:${sec
      .toString()
      .padStart(2, "0")}`;
  };

  if (!currentTrack && supremeCycle.length === 0) return null;

  const handleSafeMove = (direction) => {
    const now = Date.now();
    if (now - lastClickRef.current < 300) return;
    lastClickRef.current = now;

    if (direction === "next") {
      handleNext();
    } else {
      handlePrev();
    }
  };

  return (
    <main
      className={`fixed bottom-[50px] lg:bottom-0 left-0 w-screen z-[1000] p-0 select-none
            ${isFullHeight ? "h-screen" : "h-[100px] lg:h-[108px]"}
        `}
    >
      <div
        className={`absolute z-[50] bg-[#070D17]/50 backdrop-blur-md w-full h-full transition-opacity duration-500 ${
          isQueuePopupOpen || isVersionsPopupOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 delay-300 pointer-events-none"
        }`}
        onClick={() => {
          setIsQueuePopupOpen(false);
          setIsVersionsPopupOpen(false);
        }}
      />

      <PlayerQueue
        isOpen={isQueuePopupOpen}
        onClose={() => setIsQueuePopupOpen(false)}
        autoPlay={autoPlay}
        setAutoPlay={setAutoPlay}
      />

      <PlayerVersions
        isOpen={isVersionsPopupOpen}
        onClose={() => setIsVersionsPopupOpen(false)}
        track={currentTrack}
        editIndex={editIndex}
      />

      <div
        className="fixed bottom-[40px] lg:bottom-0 left-0 w-screen h-fit z-[4000] animate-bottom-to-top bg-transparent p-0 select-none"
        role="region"
        style={{ boxShadow: "0px -4px 22px 0px #000000A6" }}
        aria-label="Audio Player"
      >
        <div className="w-full z-[4000] bg-[#070D17]/50 backdrop-blur-md">
          <div className="relative flex items-center justify-between w-full h-full text-white">
            <div className="flex w-full flex-col lg:flex-row">
              {/* LEFT INFO */}
              <div className="flex items-center gap-0 xs:gap-1 sm:gap-2 order-2 lg:order-1 min-w-0 max-w-full overflow-hidden min-[1912px]:flex-[2]">
                {/* COVER IMAGE */}
                <div className="relative block w-full sm:w-[108px] h-[70px] sm:h-[108px] flex-shrink-0 max-w-[84px] sm:max-w-[108px] overflow-hidden">
                  <img
                    key="logo-base"
                    src={LogoImage}
                    alt="Logo"
                    className={`absolute inset-0 w-full h-full hidden sm:block object-contain p-3 transition-opacity duration-200
      ${
        coverImageReady && coverImage?.url !== LogoImage
          ? "opacity-0"
          : "opacity-100"
      }`}
                    decoding="async"
                  />
                  {coverImage?.url && coverImage.url !== LogoImage && (
                    <img
                      key={coverKey} // force remount on URL change (prevents style carry-over)
                      src={`${coverImage.url}${
                        coverImage.url.includes("?") ? "&" : "?"
                      }v=${coverKey}`}
                      alt="Cover"
                      onLoad={() => setCoverImageReady(true)}
                      onError={() => {
                        setCoverImage({ error: true, url: LogoImage });
                        setCoverImageReady(false);
                      }}
                      className={`absolute inset-0 w-full h-full hidden sm:block object-cover transition-opacity duration-200
        ${coverImageReady ? "opacity-100" : "opacity-0"}`}
                      decoding="async"
                      loading="eager"
                    />
                  )}

                  {/* Mobile Blurred BG */}
                  <img
                    key="mobile-logo-blur"
                    src={LogoImage}
                    alt=""
                    className={`absolute left-0 top-0 sm:hidden w-[84px] h-full object-contain blur-sm brightness-[0.4] contrast-100 transition-opacity duration-200
      ${
        coverImageReady && coverImage?.url !== LogoImage
          ? "opacity-0"
          : "opacity-100"
      }`}
                    style={{
                      WebkitMaskImage:
                        "linear-gradient(to right, black 70%, transparent)",
                      maskImage:
                        "linear-gradient(to right, black 70%, transparent)",
                      WebkitMaskRepeat: "no-repeat",
                      maskRepeat: "no-repeat",
                    }}
                    decoding="async"
                  />

                  {coverImage?.url && coverImage.url !== LogoImage && (
                    <img
                      key={`mobile-${coverKey}`}
                      src={coverImage.url}
                      alt=""
                      onLoad={() => setCoverImageReady(true)}
                      onError={() => {
                        setCoverImage({ error: true, url: LogoImage });
                        setCoverImageReady(false);
                      }}
                      className={`absolute left-0 top-0 sm:hidden w-[84px] h-full object-cover blur-sm brightness-[0.4] contrast-100 transition-opacity duration-200
        ${coverImageReady ? "opacity-100" : "opacity-0"}`}
                      style={{
                        WebkitMaskImage:
                          "linear-gradient(to right, black 70%, transparent)",
                        maskImage:
                          "linear-gradient(to right, black 70%, transparent)",
                        WebkitMaskRepeat: "no-repeat",
                        maskRepeat: "no-repeat",
                      }}
                      decoding="async"
                      loading="eager"
                    />
                  )}
                </div>

                {/* Overlayed Content (mobile only) */}
                <div className="absolute left-0 translate-y-[20%] top-1 z-10 h-full flex items-center sm:hidden w-[calc(100vw-160px)] [@media(min-width:375px)]:w-[calc(100vw-280px)] pointer-events-none">
                  <div className="w-full pl-3 overflow-hidden">
                    <p className="text-sm text-gradient font-normal truncate">
                      {artists}
                    </p>
                    <p className="text-base font-bold truncate text-secondary leading-[135%]">
                      {trackTitle}
                    </p>
                    {editTitle && (
                      <p className="text-tertiary truncate text-sm">
                        ({editTitle})
                      </p>
                    )}
                  </div>
                </div>

                {/* TEXT INFO (desktop) */}
                <div className="min-w-0 flex-1 pl-3 sm:block hidden overflow-hidden">
                  <p className="text-[10px] sm:text-sm text-gradient font-normal truncate">
                    {artists}
                  </p>
                  <p className="text-sm sm:text-base font-bold truncate text-secondary leading-[135%] my-[1px]">
                    {trackTitle}
                  </p>
                  {editTitle && (
                    <p className="text-tertiary truncate text-[10px] sm:text-sm">
                      ({editTitle})
                    </p>
                  )}
                </div>

                <div className="flex-1 sm:hidden" />

                {/* VERSION + QUEUE (always visible) */}
                <div className="flex items-center gap-1 px-2 flex-shrink-0">
                  {/* VERSION DROPDOWN */}
                  <div
                    onClick={() => {
                      setIsVersionsPopupOpen((prev) => !prev);
                      setIsQueuePopupOpen(false);
                    }}
                    className="grid grid-cols-[4fr_1fr] justify-start items-center w-[30px] xs:w-[80px] sm:w-[120px] md:w-[160px] lg:w-[140px] xl:w-[160px] h-8 xs:h-10 px-1 xs:px-2 sm:px-4 py-0 xs:py-3 rounded-[5px] border border-[#253B51] cursor-pointer flex-shrink-0"
                  >
                    <p className="hidden xs:block text-xs sm:text-sm text-secondary font-normal truncate">
                      {trackVersion || ""}
                    </p>
                    <div className="w-3 h-3 py-1 px-1">
                      <svg
                        width="11"
                        height="7"
                        viewBox="0 0 11 7"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M10.1447 5.54883L6.38058 1.78473C5.93605 1.34019 5.20863 1.34019 4.7641 1.78473L1 5.54883"
                          stroke="white"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* QUEUE ICON */}
                  <div
                    onClick={() => {
                      setIsQueuePopupOpen((prev) => !prev);
                      setIsVersionsPopupOpen(false);
                    }}
                    className="p-1 xs:p-2 cursor-pointer hover:opacity-70 active:opacity-50 transition flex-shrink-0"
                  >
                    <svg
                      width="15"
                      height="13"
                      className="xs:w-[19px] xs:h-[17px]"
                      viewBox="0 0 19 17"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.894531 8.5H17.3945M0.894531 12.25H17.3945M0.894531 16H17.3945M2.76953 1H15.5195C16.0168 1 16.4937 1.19754 16.8454 1.54917C17.197 1.90081 17.3945 2.375 17.3945 2.875C17.3945 3.37228 17.197 3.84919 16.8454 4.20083C16.4937 4.55246 16.0168 4.75 15.5195 4.75H2.76953C2.27225 4.75 1.79534 4.55246 1.44371 4.20083C1.09208 3.84919 0.894531 3.37228 0.894531 2.875C0.894531 2.37772 1.09208 1.90081 1.44371 1.54917C1.79534 1.19754 2.27225 1 2.76953 1Z"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                </div>

                {/* MOBILE PLAYER CONTROLS (always visible) */}
                <div className="flex min-w-fit justify-end items-center px-2 flex-shrink-0 lg:hidden">
                  <div className="grid grid-cols-3 h-full w-fit gap-1 xs:gap-2">
                    <button
                      onClick={() => handleSafeMove("prev")}
                      aria-label="Previous"
                      className="p-1 xs:p-2 hover:opacity-70 active:scale-75 transition"
                    >
                      <img
                        src={BackwardIcon}
                        alt=""
                        className="w-3 h-3 xs:w-4 xs:h-4"
                      />
                    </button>

                    {!audioLoaded && !isPlaying ? (
                      <div className="flex items-center justify-center h-full opacity-50">
                        <img
                          src={LoadingIcon}
                          alt="Loading"
                          className="w-3 h-3 xs:w-4 xs:h-4 animate-spin"
                        />
                      </div>
                    ) : (
                      <button
                        onClick={togglePlay}
                        aria-label={isPlaying ? "Pause" : "Play"}
                        className="p-1 xs:p-2 hover:opacity-70 active:scale-75 transition"
                      >
                        {isPlaying ? (
                          <img
                            src={PauseIcon}
                            alt="Pause"
                            className="w-3 h-3 xs:w-4 xs:h-4"
                          />
                        ) : (
                          <img
                            src={PlayIcon}
                            alt="Play"
                            className="w-3 h-3 xs:w-4 xs:h-4"
                          />
                        )}
                      </button>
                    )}

                    <button
                      onClick={() => handleSafeMove("next")}
                      aria-label="Next"
                      className="p-1 xs:p-2 hover:opacity-70 active:scale-75 transition"
                    >
                      <img
                        src={ForwardIcon}
                        alt=""
                        className="w-3 h-3 xs:w-4 xs:h-4"
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* CENTER CONTROLS (desktop) */}
              <div className="hidden lg:flex justify-center items-center order-2 flex-none px-4">
                <div className="grid grid-cols-3 h-full w-fit gap-1 sm:gap-3 md:gap-4 lg:gap-3 xl:gap-4">
                  <button
                    onClick={() => handleSafeMove("prev")}
                    aria-label="Previous"
                    className="p-1 sm:p-2 md:p-3 lg:p-2 xl:p-3 hover:opacity-70 active:scale-75 transition"
                  >
                    <img src={BackwardIcon} alt="" className="w-5 h-5" />
                  </button>

                  {!audioLoaded && !isPlaying ? (
                    <div className="flex items-center justify-center h-full opacity-50">
                      <img
                        src={LoadingIcon}
                        alt="Loading"
                        className="w-5 h-5 animate-spin"
                      />
                    </div>
                  ) : (
                    <button
                      onClick={togglePlay}
                      aria-label={isPlaying ? "Pause" : "Play"}
                      className="p-1 sm:p-2 md:p-3 lg:p-2 xl:p-3 hover:opacity-70 active:scale-75 transition"
                    >
                      {isPlaying ? (
                        <img src={PauseIcon} alt="Pause" className="w-5 h-5" />
                      ) : (
                        <img src={PlayIcon} alt="Play" className="w-5 h-5" />
                      )}
                    </button>
                  )}

                  <button
                    onClick={() => handleSafeMove("next")}
                    aria-label="Next"
                    className="p-1 sm:p-2 md:p-3 lg:p-2 xl:p-3 hover:opacity-70 active:scale-75 transition"
                  >
                    <img src={ForwardIcon} alt="" className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* WAVEFORM SECTION */}
              <div className="relative flex items-center order-1 lg:order-3 flex-1 w-full p-3 py-2 md:py-3 lg:py-5 lg:px-0">
                <div className="flex flex-col w-full lg:py-3 xl:ms-3">
                  <div className="relative w-full">
                    {!audioLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center z-10 opacity-50">
                        <img
                          src={LoadingIcon}
                          alt="Loading"
                          className="w-5 h-5 animate-spin"
                        />
                      </div>
                    )}
                    <div
                      ref={waveformLgRef}
                      className="h-[30px] w-full rounded-md cursor-pointer overflow-hidden relative"
                      aria-hidden="true"
                    />

                    {/* Preview limit visual indicator for non-subscribers */}
                    {!hasActiveSubscription && audioLoaded && duration > 0 && (
                      <>
                        {/* Greyed overlay on locked portion (45s onwards) */}
                        <div
                          className="absolute top-0 h-[30px] bg-black/60 pointer-events-none rounded-r-md z-10"
                          style={{
                            left: `${(45 / duration) * 100}%`,
                            width: `${((duration - 45) / duration) * 100}%`,
                          }}
                        />

                        {/* Vertical boundary line at 45 seconds */}
                        <div
                          className="absolute top-0 h-[30px] w-[2px] bg-cyan-400 z-20 pointer-events-none"
                          style={{
                            left: `${(45 / duration) * 100}%`,
                          }}
                        />

                        {/* Locked section with tooltip */}
                        <div
                          className="absolute top-0 h-[30px] cursor-not-allowed z-30"
                          style={{
                            left: `${(45 / duration) * 100}%`,
                            width: `${((duration - 45) / duration) * 100}%`,
                          }}
                          onMouseEnter={() => setShowLockedTooltip(true)}
                          onMouseLeave={() => setShowLockedTooltip(false)}
                        >
                          {showLockedTooltip && (
                            <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-3 py-1.5 rounded whitespace-nowrap shadow-lg z-50">
                              Subscribe for full access
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-900"></div>
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="hidden lg:flex justify-between w-full text-[10px] text-gradient mt-1">
                    <span>{formatTime(currentTime)}</span>
                    <span>{formatTime(duration)}</span>
                  </div>
                </div>
              </div>

              {/* RIGHT AUTOPLAY TOGGLE */}
              <div className="hidden lg:flex flex-none items-center gap-4 px-5 order-4">
                {/* Subscribe CTA Badge for non-subscribers (show only after modal appeared this session) */}
                {!hasActiveSubscription && modalSeenInSession && (
                  <button
                    onClick={() => navigate("/membership")}
                    className="text-xs bg-gradient-to-r from-cyan-500 to-blue-500 text-black px-3 py-1.5 rounded-full font-semibold hover:opacity-90 transition whitespace-nowrap"
                  >
                    Subscribe for Full Access
                  </button>
                )}

                <div className="flex items-center gap-1">
                  <span className="text-sm text-secondary">Autoplay</span>
                  <label className="relative inline-flex items-center cursor-pointer w-11 h-6">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={autoPlay}
                      onChange={() => setAutoPlay((prev) => !prev)}
                      aria-label="Toggle Autoplay"
                    />
                    <div className="w-full h-full bg-[#1E1E1E] rounded-full peer-checked:bg-cyan-500 transition-colors" />
                    <div className="absolute top-1 left-1 w-4 h-4 bg-white rounded-full transition-transform peer-checked:translate-x-[20px]" />
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal
        isOpen={showSubscriptionModal && !hasActiveSubscription}
        onClose={() => setShowSubscriptionModal(false)}
        previewCount={previewCount}
      />
    </main>
  );
};

export default SupremeEditsPlayer;
